import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { qualityControlApi } from '@/services/quality-control-api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Spinner } from '@/components/ui/Spinner';
import { Tabs, TabList, TabTrigger, TabContent } from '@/components/ui/Tabs';
import type { ColumnDef } from '@/components/ui/DataTable';
import type { IOrphanedStory, ITeamHygieneScore, RagStatus } from '@skystone/shared';
import { PageContainer } from '@/components/layout';
import { CalculationReferenceTab } from '@/components/quality-control/CalculationReferenceTab';

type QualityControlTab = 'operations' | 'reference';

const VALID_TABS: ReadonlySet<QualityControlTab> = new Set(['operations', 'reference']);

function parseTab(raw: string | null): QualityControlTab {
  return VALID_TABS.has(raw as QualityControlTab)
    ? (raw as QualityControlTab)
    : 'operations';
}

const RAG_VARIANT_MAP: Record<RagStatus, 'success' | 'warning' | 'error'> = {
  green: 'success',
  amber: 'warning',
  red: 'error',
};

const RAG_LABEL_MAP: Record<RagStatus, string> = {
  green: 'Good',
  amber: 'Needs Attention',
  red: 'Action Required',
};

const orphanedStoryColumns: ColumnDef<IOrphanedStory>[] = [
  { id: 'issueKey', header: 'Issue Key', accessor: 'issueKey', sortable: true, filter: { type: 'text', placeholder: 'Filter key...' } },
  { id: 'issueName', header: 'Story Name', accessor: 'issueName', sortable: true, filter: { type: 'text', placeholder: 'Filter name...' } },
  { id: 'teamName', header: 'Team', accessor: 'teamName', sortable: true, filter: { type: 'text', placeholder: 'Filter team...' } },
  { id: 'assigneeName', header: 'Assignee', accessor: 'assigneeName', sortable: true, filter: { type: 'text', placeholder: 'Filter assignee...' } },
  { id: 'productName', header: 'Product Area', accessor: 'productName', sortable: true, filter: { type: 'text', placeholder: 'Filter product...' } },
];

const hygieneColumns: ColumnDef<ITeamHygieneScore>[] = [
  { id: 'teamName', header: 'Team', accessor: 'teamName', sortable: true },
  { id: 'productName', header: 'Product', accessor: 'productName', sortable: true },
  {
    id: 'epicCoverage',
    header: 'Stories without Epic / Total',
    accessor: 'storiesWithoutEpic',
    cell: (_value: unknown, row: ITeamHygieneScore) => `${row.storiesWithoutEpic} / ${row.totalStories}`,
    sortable: true,
  },
  {
    id: 'percentage',
    header: '%',
    accessor: 'percentage',
    cell: (value: unknown) => `${value}%`,
    sortable: true,
    align: 'right',
  },
  {
    id: 'ragStatus',
    header: 'Status',
    accessor: 'ragStatus',
    cell: (value: unknown) => {
      const status = value as RagStatus;
      return <Badge variant={RAG_VARIANT_MAP[status]}>{RAG_LABEL_MAP[status]}</Badge>;
    },
    sortable: true,
    align: 'center',
  },
];

function OrphanedStoriesPanel({ stories, loading, error }: Readonly<{
  stories: IOrphanedStory[] | null;
  loading: boolean;
  error: string | null;
}>) {
  const data = stories ?? [];
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <CardTitle className="text-lg">Stories Without Epic</CardTitle>
          {!loading && data.length > 0 && (
            <Badge variant="error">{data.length}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ErrorBanner message={error} />
        <DataTable<IOrphanedStory>
          data={data}
          columns={orphanedStoryColumns}
          keyExtractor={(row) => row.issueKey}
          isLoading={loading}
          emptyMessage="No orphaned stories found. All stories have an epic assigned."
          defaultPageSize={25}
        />
      </CardContent>
    </Card>
  );
}

function TeamHygienePanel({ scores, loading, error }: Readonly<{
  scores: ITeamHygieneScore[] | null;
  loading: boolean;
  error: string | null;
}>) {
  const data = scores ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Team Hygiene Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <ErrorBanner message={error} />
        <DataTable<ITeamHygieneScore>
          data={data}
          columns={hygieneColumns}
          keyExtractor={(row) => row.teamId}
          isLoading={loading}
          emptyMessage="No team hygiene data available."
          enablePagination={false}
        />
      </CardContent>
    </Card>
  );
}

export function QualityControlPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('productId') ?? undefined;
  const teamId = searchParams.get('teamId') ?? undefined;
  const activeTab = parseTab(searchParams.get('tab'));
  const filters = useMemo(() => ({ productId, teamId }), [productId, teamId]);

  const { data: stories, loading: storiesLoading, error: storiesError, refetch: refetchStories } =
    useApi(() => qualityControlApi.getOrphanedStories(filters), [productId, teamId]);

  const { data: overview, loading: overviewLoading, error: overviewError, refetch: refetchOverview } =
    useApi(() => qualityControlApi.getOverview(filters), [productId, teamId]);

  const { data: filterOptions } = useApi(() => qualityControlApi.getFilterOptions(), []);

  const handleRefresh = useCallback(() => {
    refetchStories();
    refetchOverview();
  }, [refetchStories, refetchOverview]);

  const handleProductChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set('productId', value);
    } else {
      next.delete('productId');
    }
    next.delete('teamId');
    setSearchParams(next);
  };

  const availableTeams = useMemo(() => {
    if (!filterOptions?.teams) return [];
    if (!productId) return filterOptions.teams;
    return filterOptions.teams.filter((t) => !t.productId || t.productId === productId);
  }, [filterOptions?.teams, productId]);

  const handleTeamChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set('teamId', value);
    } else {
      next.delete('teamId');
    }
    setSearchParams(next);
  };

  const handleTabChange = useCallback(
    (next: string) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'operations') {
        params.delete('tab');
      } else {
        params.set('tab', next);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const lastRefreshed = overview?.lastRefreshedAt
    ? new Date(overview.lastRefreshedAt).toLocaleString()
    : null;

  const isLoading = storiesLoading || overviewLoading;
  const showRefreshAction = activeTab === 'operations';

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title="Quality Control"
        subtitle={lastRefreshed && showRefreshAction ? `Last refreshed: ${lastRefreshed}` : undefined}
        action={
          showRefreshAction ? (
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? <Spinner size="sm" className="mr-2" /> : null}
              Refresh
            </Button>
          ) : undefined
        }
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabList>
          <TabTrigger value="operations">Operations</TabTrigger>
          <TabTrigger value="reference">Reference</TabTrigger>
        </TabList>

        <TabContent value="operations" className="space-y-6">
          <div className="flex flex-wrap gap-4">
            <select
              value={productId ?? ''}
              onChange={(e) => handleProductChange(e.target.value)}
              className="px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Filter by product"
            >
              <option value="">All Products</option>
              {filterOptions?.products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            <select
              value={teamId ?? ''}
              onChange={(e) => handleTeamChange(e.target.value)}
              className="px-3 py-2 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Filter by team"
            >
              <option value="">All Teams</option>
              {availableTeams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <OrphanedStoriesPanel stories={stories} loading={storiesLoading} error={storiesError} />
          <TeamHygienePanel scores={overview?.teamScores ?? null} loading={overviewLoading} error={overviewError} />
        </TabContent>

        <TabContent value="reference">
          <CalculationReferenceTab />
        </TabContent>
      </Tabs>
    </PageContainer>
  );
}
